<template>
  <div class="table-responsive">
    <table
      data-test="examinations-table"
      class="table table-hover table-actions-bar cases-list"
    >
      <thead class="sortable-table-header">
        <tr>
          <th
            v-if="$store.state.user.has_ai_prioritization"
            data-test="examinations-column-ai-priority"
            class="consultations-list-icon"
          ></th>
          <th
            v-if="$can('prioritise_cases')"
            data-test="examinations-column-bookmark"
            class="consultations-list-icon"
          ></th>
          <th
            v-if="$can('prioritise_cases')"
            data-test="examinations-column-flag"
            class="consultations-list-icon"
          ></th>
          <th
            v-if="$can('prioritise_cases')"
            data-test="examinations-column-priority"
            class="consultations-list-icon"
          ></th>
          <th class="consultations-list-icon"></th>
          <th>{{ trans("evaluation.tableColumnCaseNo") }}</th>
          <th
            class="sort-by"
            :class="sortBySelection === 'status' ? 'table-active' : ''"
            @click="$emit('sort', 'status', $event)"
          >
            {{ trans("evaluation.tableColumnStatus") }}
            <i class="fas fa-caret-down"></i>
          </th>
          <th
            v-if="$store.state.user.has_svf_product"
            data-test="examinations-column-svf"
          >
            {{ trans("examination.svfLabel") }}
          </th>
          <th>{{ trans("evaluation.tableColumnProduct") }}</th>
          <th
            v-if="!$store.state.user.hidePatientFields"
            data-test="examinations-column-personal-no"
          >
            {{ trans("evaluation.tableColumnPersonalNo") }}
          </th>
          <th
            class="sort-by created-at"
            :class="sortBySelection === 'createdAt' ? 'table-active' : ''"
            @click="$emit('sort', 'createdAt', $event)"
          >
            {{ trans("evaluation.tableColumnCreatedAt") }}
            <i class="createdAtSortIcon fas fa-caret-down"></i>
          </th>
          <th>{{ trans("evaluation.tableColumnCreatedBy") }}</th>
          <th
            class="sort-by"
            :class="sortBySelection === 'centerId' ? 'table-active' : ''"
            @click="$emit('sort', 'centerId', $event)"
          >
            {{ trans("evaluation.tableColumnCenter") }}
            <i class="fas fa-caret-down"></i>
          </th>
          <th>{{ trans("evaluation.tableColumnConsultReply") }}</th>
          <th v-if="$can('consult')">
            {{ trans("evaluation.tableColumnInternalConsultReply") }}
          </th>
          <th v-if="$can('consult')">
            {{ trans("evaluation.tableColumnMessages") }}
          </th>
        </tr>
      </thead>
      <tbody>
        <tr v-if="userHasNoProducts">
          <td colspan="100%" class="no-products-message">
            {{ userHasNoProductsMessage }}
          </td>
        </tr>
        <tr
          v-for="(examination, index) in examinations"
          :key="index"
          :data-test="`examination-row-${examination.caseNo}`"
          :class="{
            opened: examination.hasRead,
            last: isLastOpend(examination),
          }"
          @click="$emit('open', examination)"
        >
          <td
            v-if="$store.state.user.has_ai_prioritization"
            class="consultations-list-icon"
          >
            <!-- implement real examination prop when it exist -->
            <ai-priority-icon
              v-if="examination.aiPriority"
              :priority="examination.aiPriority"
            ></ai-priority-icon>
          </td>
          <td v-if="$can('prioritise_cases')" class="consultations-list-icon">
            <bookmark-icon v-if="examination.isBookmarked" />
          </td>
          <td v-if="$can('prioritise_cases')" class="consultations-list-icon">
            <flag-icon v-if="examination.isFlagged" />
          </td>
          <td v-if="$can('prioritise_cases')" class="consultations-list-icon">
            <priority-icon v-if="examination.isPrioritized" />
          </td>
          <td class="consultations-list-icon">
            <CdrFlagStatusBadge v-if="examination.hasPendingCdrFlag" />
          </td>
          <td>{{ examination.caseNo }}</td>
          <td>
            <case-status
              :examination="examination"
              :hide-label="true"
            ></case-status>
          </td>
          <td v-if="$store.state.user.has_svf_product">
            {{ examination.isSvf ?? "–" }}
          </td>
          <td>{{ examination.type }}</td>
          <td v-if="!$store.state.user.hidePatientFields">
            {{ examination.patient.ssn }}
          </td>
          <td v-if="$store.state.dermicusPlatform === 'stockholm'">
            {{ examination.photographedAt ? examination.photographedAt : "–" }}
          </td>
          <td v-else>{{ examination.createdAt }}</td>
          <td :title="examination.createdUserFullName">
            {{ examination.createdUser }}
          </td>
          <td>{{ examination.center }}</td>
          <td>{{ examination.lastComment }}</td>
          <td v-if="$can('consult')">
            <span
              v-for="(
                consultCommentUsers, commentIndex
              ) in examination.consultCommentUsers"
              :key="commentIndex"
              v-dompurify-html:[DOMPURIFY_CONFIG_NAME.consultCommentUsers]="
                consultCommentUsers
              "
            ></span>
          </td>
          <td v-if="$can('consult')">
            <consensus-messages-count
              :all-messages="examination.totalMessages"
              :unread-messages="examination.notifications"
            ></consensus-messages-count>
          </td>
        </tr>
      </tbody>
    </table>
  </div>
</template>

<script>
import ConsensusMessagesCount from "../Utils/ConsensusMessagesCount.vue";
import AiPriorityIcon from "../Utils/AiFeatures/AiPriorityIcon.vue";
import PriorityIcon from "../Utils/ActionIcons/PriorityIcon.vue";
import CdrFlagStatusBadge from "../ClinicalDataRemoval/CdrFlagStatusBadge.vue";
import FlagIcon from "../Utils/ActionIcons/FlagIcon.vue";
import BookmarkIcon from "../Utils/ActionIcons/BookmarkIcon.vue";
import CaseStatus from "../Examinations/Examination/ExaminationComponents/components/CaseStatus.vue";
import { DOMPURIFY_CONFIG_NAME } from "../../dompurify";

export default {
  components: {
    ConsensusMessagesCount,
    AiPriorityIcon,
    PriorityIcon,
    CdrFlagStatusBadge,
    FlagIcon,
    BookmarkIcon,
    CaseStatus,
  },
  props: {
    examinations: { type: Array, required: true },
    sortBySelection: { type: String, required: true },
    userHasNoProducts: { type: Boolean, required: true },
    userHasNoProductsMessage: { type: String, required: true },
  },
  emits: ["sort", "open"],
  data() {
    return { DOMPURIFY_CONFIG_NAME };
  },
  methods: {
    isLastOpend: function (examination) {
      return (
        localStorage.getItem("last_visited_examination_id") == examination.id
      );
    },
  },
};
</script>

<style lang="scss" scoped>
.consultations-list-icon {
  padding: 0px 2px;
}

.no-products-message {
  text-align: center;
  padding: 40px 0;
  font-weight: 300;
}
</style>
